import { Button, Card, Flex, Text, Tag } from '@applyboard/crystal-ui'
import { PropsWithChildren, Ref } from 'react'
import { ApplicationFormTitle } from './ApplicationFormTitle'
import styled from '@emotion/styled'
import { down } from '../../../utils'
import { useGetTimelineItems } from '../../../hooks'
import { useGetApplicationPageProps } from '../../../hooks/useGetApplicationPageProps'
import { getDocumentProcessingConfigPassportEnabled } from '../../../utils/specificFieldValidations'

type ApplicationFormCardProps = {
  disabled?: boolean
  cardNumber: number
  totalCards: number
  icon: string
  title: string
  isLoading?: boolean
  submitButtonLabel?: string
  onSubmitApplication?: () => void
  onPreviousTabButton?: () => void
  onNextTabButton?: () => void
  focusRef?: Ref<HTMLButtonElement> | null
}

const HideOnMobile = styled('div')({
  [down('md')]: {
    display: 'none',
  },
})

export function ApplicationFormCard(props: PropsWithChildren<ApplicationFormCardProps>) {
  const { applicationPageProps } = useGetApplicationPageProps()

  const isAiProcessingEnabled = getDocumentProcessingConfigPassportEnabled(
    applicationPageProps?.documentProcessingConfig,
  )

  const isPersonalInformationCard = props.cardNumber === 1
  return (
    <Card aria-label={props.title}>
      <Card.Header>
        <Flex justify="between" align="center" hideBelow="md">
          <Flex direction="column">
            <Text variant="bodyS">
              {props.cardNumber} of {props.totalCards}
            </Text>
            <ApplicationFormTitle icon={props.icon} title={props.title} />
          </Flex>
          {isPersonalInformationCard && isAiProcessingEnabled ? (
            <Tag intent="primary" size="sm" icon="fill">
              This section is AI enabled
            </Tag>
          ) : null}
        </Flex>
      </Card.Header>
      <HideOnMobile>
        <Card.Divider mt={2} mb={2} />
      </HideOnMobile>
      <Flex direction="column" gap={4}>
        {props.children}
        <Flex hideAbove="md" direction="column" justify="end" gap={4}>
          <FormSubmitButtonGroup
            cardNumber={props.cardNumber}
            disabled={props.disabled}
            fullWidth
            isLoading={props.isLoading}
            submitButtonLabel={props.submitButtonLabel}
            onSubmitApplication={props.onSubmitApplication}
          />
        </Flex>
        <Flex pt={4} hideBelow="md" gap={4} justify={'end'}>
          <FormSubmitButtonGroup
            cardNumber={props.cardNumber}
            disabled={props.disabled}
            isLoading={props.isLoading}
            submitButtonLabel={props.submitButtonLabel}
            onSubmitApplication={props.onSubmitApplication}
            onPreviousTabButton={props.onPreviousTabButton}
            onNextTabButton={props.onNextTabButton}
            focusRef={props.focusRef}
          />
        </Flex>
      </Flex>
    </Card>
  )
}

type FormSubmitButtonGroupProps = Readonly<{
  cardNumber: number
  disabled?: boolean
  fullWidth?: boolean
  isLoading?: boolean
  submitButtonLabel?: string
  onSubmitApplication?: () => void
  onPreviousTabButton?: ApplicationFormCardProps['onPreviousTabButton']
  onNextTabButton?: ApplicationFormCardProps['onNextTabButton']
  focusRef?: Ref<HTMLButtonElement> | null
}>

function FormSubmitButtonGroup(props: FormSubmitButtonGroupProps) {
  const { timelineItems } = useGetTimelineItems(null)
  const lastCardNumber = timelineItems.at(-1)?.cardNumber ?? 6

  if (props.disabled) {
    return (
      <>
        {props.cardNumber > 1 && props.onPreviousTabButton ? (
          <PreviousButton
            disabled={props.isLoading}
            onPreviousTabButton={props.onPreviousTabButton}
          />
        ) : null}
        {props.cardNumber < lastCardNumber && props.onNextTabButton ? (
          <Button
            ref={props.focusRef}
            intent="primary"
            onClick={props.onNextTabButton}
            width={props.fullWidth ? 'fill' : undefined}
            loading={props.isLoading}
          >
            Continue
          </Button>
        ) : null}
      </>
    )
  }

  if (props.onSubmitApplication) {
    return (
      <>
        <Button
          type="submit"
          intent="secondary"
          emphasis="outlined"
          loading={props.isLoading}
          width={props.fullWidth ? 'fill' : undefined}
        >
          Save
        </Button>
        <Button
          intent="primary"
          disabled={props.isLoading}
          onClick={props.onSubmitApplication}
          width={props.fullWidth ? 'fill' : undefined}
        >
          {props.submitButtonLabel ?? 'Save & Submit'}
        </Button>
      </>
    )
  }

  return (
    <>
      {props.cardNumber > 1 ? (
        <PreviousButton
          disabled={props.isLoading}
          onPreviousTabButton={props.onPreviousTabButton}
        />
      ) : null}
      <Button
        type="submit"
        intent="primary"
        loading={props.isLoading}
        width={props.fullWidth ? 'fill' : undefined}
      >
        Save & Continue
      </Button>
    </>
  )
}

type PreviousButtonProps = Readonly<{
  disabled?: boolean
  onPreviousTabButton?: ApplicationFormCardProps['onPreviousTabButton']
}>

function PreviousButton(props: PreviousButtonProps) {
  if (!props.onPreviousTabButton) {
    return null
  }

  return (
    <Button
      disabled={props.disabled}
      type="button"
      intent="primary"
      emphasis="outlined"
      onClick={props.onPreviousTabButton}
    >
      Previous
    </Button>
  )
}
